import { useState } from 'react'
import type { ReviewProposedRequest, FieldChangeDto, SampleRowWithVendorProposed } from '../api/types'
import { PendingChangeDiff } from './PendingChangeToggle'
import { useAuth } from '../auth/AuthContext'

interface PendingChangeReviewProps {
  sampleId: string
  vendorProposed: Record<string, unknown>
  vendorSource: string | null
  vendorProposedAt: string | null
  onReviewComplete: () => void
  compact?: boolean
  /** Current row data for resolving FK display names */
  rowData?: Record<string, unknown>
}

// Convert vendor_proposed to FieldChangeDto format for the diff display
function convertToChanges(
  vendorProposed: Record<string, unknown>,
  rowData?: Record<string, unknown>
): Record<string, FieldChangeDto> {
  const changes: Record<string, FieldChangeDto> = {}

  for (const [field, newValue] of Object.entries(vendorProposed)) {
    // Skip extra fields (they start with _extra_)
    if (field.startsWith('_extra_')) {
      continue
    }

    const oldValue = rowData?.[field] ?? null
    changes[field] = {
      old: oldValue,
      new: newValue,
    }
  }

  return changes
}

// Get extra fields from vendor_proposed (those starting with _extra_)
function getExtraFields(vendorProposed: Record<string, unknown>): Record<string, unknown> {
  const extra: Record<string, unknown> = {}

  for (const [field, value] of Object.entries(vendorProposed)) {
    if (field.startsWith('_extra_')) {
      // Remove the _extra_ prefix for display
      const displayName = field.replace(/^_extra_/, '')
      extra[displayName] = value
    }
  }

  return extra
}

export function PendingChangeReview({
  sampleId,
  vendorProposed,
  vendorSource,
  vendorProposedAt,
  onReviewComplete,
  compact = false,
  rowData,
}: PendingChangeReviewProps) {
  const { apiClient } = useAuth()
  const [showDiff, setShowDiff] = useState(true) // Always show diff by default
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const changes = convertToChanges(vendorProposed, rowData)
  const extraFields = getExtraFields(vendorProposed)
  const fieldCount = Object.keys(changes).length
  const extraFieldCount = Object.keys(extraFields).length

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const handleAction = async (action: 'accept' | 'reject') => {
    if (!apiClient) {
      setError('Not authenticated')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const request: ReviewProposedRequest = { action }
      await apiClient.reviewProposed(sampleId, request)

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('pendingChangesUpdated'))

      onReviewComplete()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review changes')
    } finally {
      setIsLoading(false)
    }
  }

  if (compact) {
    return (
      <div className="pending-review-compact">
        <div className="pending-review-header">
          <span className="pending-dot" />
          <span className="pending-review-source">
            Update from <strong>{vendorSource || 'Vendor'}</strong>
          </span>
          <span className="pending-review-count">
            {fieldCount} field{fieldCount !== 1 ? 's' : ''}
            {extraFieldCount > 0 && ` + ${extraFieldCount} extra`}
          </span>
          <button
            type="button"
            className="pending-review-toggle-btn"
            onClick={() => setShowDiff(!showDiff)}
          >
            {showDiff ? 'Hide' : 'View'}
          </button>
        </div>

        {showDiff && (
          <div className="pending-review-diff">
            <PendingChangeDiff changes={changes} rowData={rowData} />
            {extraFieldCount > 0 && (
              <div className="pending-review-extra">
                <div className="pending-review-extra-header">
                  Extra vendor fields ({extraFieldCount}):
                </div>
                {Object.entries(extraFields).map(([field, value]) => (
                  <div key={field} className="pending-review-extra-field">
                    <span className="pending-review-extra-name">{field}:</span>
                    <span className="pending-review-extra-value">{String(value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {error && <div className="pending-review-error">{error}</div>}

        <div className="pending-review-actions">
          <button
            type="button"
            className="pending-review-btn pending-review-btn-accept"
            onClick={() => handleAction('accept')}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Accept'}
          </button>
          <button
            type="button"
            className="pending-review-btn pending-review-btn-reject"
            onClick={() => handleAction('reject')}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Reject'}
          </button>
        </div>
      </div>
    )
  }

  // Full view for sample detail
  return (
    <div className="pending-review-full">
      <div className="pending-review-header">
        <div className="pending-review-title">
          <span className="pending-dot-large" />
          <span>Pending Vendor Changes</span>
        </div>
        <div className="pending-review-meta">
          <span className="pending-review-source">
            From: <strong>{vendorSource || 'Vendor'}</strong>
          </span>
          {vendorProposedAt && (
            <span className="pending-review-date">
              Received: {formatDate(vendorProposedAt)}
            </span>
          )}
        </div>
      </div>

      <div className="pending-review-summary">
        {fieldCount} field change{fieldCount !== 1 ? 's' : ''}
        {extraFieldCount > 0 && `, ${extraFieldCount} extra unmapped field${extraFieldCount !== 1 ? 's' : ''}`}
      </div>

      <div className="pending-review-diff">
        <PendingChangeDiff changes={changes} rowData={rowData} />
      </div>

      {extraFieldCount > 0 && (
        <div className="pending-review-extra">
          <div className="pending-review-extra-header">
            Extra Vendor Fields (not mapped to known columns):
          </div>
          {Object.entries(extraFields).map(([field, value]) => (
            <div key={field} className="pending-review-extra-field">
              <span className="pending-review-extra-name">{field}:</span>
              <span className="pending-review-extra-value">{String(value)}</span>
            </div>
          ))}
          <div className="pending-review-extra-note">
            These fields will be stored in structured_data when accepted.
          </div>
        </div>
      )}

      {error && <div className="pending-review-error">{error}</div>}

      <div className="pending-review-warning">
        You must review these changes before editing this sample.
      </div>

      <div className="pending-review-actions">
        <button
          type="button"
          className="pending-review-btn pending-review-btn-accept"
          onClick={() => handleAction('accept')}
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : 'Accept All Changes'}
        </button>
        <button
          type="button"
          className="pending-review-btn pending-review-btn-reject"
          onClick={() => handleAction('reject')}
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : 'Reject All Changes'}
        </button>
      </div>
    </div>
  )
}

// Helper to check if a row has pending vendor changes
export function hasPendingVendorChanges(row: SampleRowWithVendorProposed): boolean {
  if (row.vendor_proposed && typeof row.vendor_proposed === 'object') {
    return Object.keys(row.vendor_proposed).length > 0
  }
  return row.has_pending_changes === true
}

// Helper to get vendor_proposed info from a row
export function getVendorProposedInfo(row: SampleRowWithVendorProposed): {
  vendorProposed: Record<string, unknown>
  vendorSource: string | null
  vendorProposedAt: string | null
} | null {
  if (!row.vendor_proposed || typeof row.vendor_proposed !== 'object') {
    return null
  }

  return {
    vendorProposed: row.vendor_proposed as Record<string, unknown>,
    vendorSource: row.vendor_proposed_source ?? null,
    vendorProposedAt: row.vendor_proposed_at ?? null,
  }
}
